import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { computeInsights } from "../../lib/internalInsights";
import { Brain, Loader2, ShieldAlert, ArrowRight, Megaphone, HeartHandshake, LifeBuoy, Database, Lightbulb, BarChart3, Settings, ShieldCheck } from "lucide-react";

/**
 * Internal AI Operations — framework + rule-based "copilots".
 *
 * IMPORTANT: Phase 4 ships these as rule-based / placeholder cards.
 * NO LLM is called. NO record is mutated. NO email is sent. NO external side
 * effects. Each copilot reads existing protected internal data and produces
 * deterministic suggestions. When a copilot has no real input yet, the card
 * renders a clearly-labeled placeholder.
 */

const COPILOTS = [
  { id: "sales",     label: "Sales AI",            icon: Megaphone,    description: "Prioritize active prospects + flag stalled deals." },
  { id: "marketing", label: "Marketing AI",        icon: BarChart3,    description: "AI Discovery + funnel insight — placeholder until session-auth /events ships." },
  { id: "success",   label: "Customer Success AI", icon: HeartHandshake, description: "Customer activity rollup + repeat-buyer signal." },
  { id: "support",   label: "Support AI",          icon: LifeBuoy,     description: "Support inbox summary — placeholder, no support inbox yet." },
  { id: "data",      label: "Data Quality AI",     icon: Database,     description: "Detect incomplete prospects (missing decision-maker / NAICS / etc.)." },
  { id: "product",   label: "Product Manager AI",  icon: Lightbulb,    description: "Feature interest + roadmap signals — placeholder." },
  { id: "revenue",   label: "Revenue AI",          icon: ShieldCheck,  description: "Pipeline → revenue rollup (CEO-only). Currently shows pipeline only; live Stripe rollup deferred to Phase 5." },
  { id: "ops",       label: "Operations AI",       icon: Settings,     description: "System health + Phase 5 hardening backlog." },
];

export default function AiOps() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null); // copilot id

  useEffect(() => {
    (async () => {
      try {
        const [pr, db] = await Promise.all([
          api.get("/prospects").then((r) => r.data).catch(() => []),
          api.get("/internal/dashboard").then((r) => r.data).catch(() => null),
        ]);
        setInsights(computeInsights({ prospects: pr || [], dashboard: db }));
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading AI Ops…</div>;

  return (
    <div className="space-y-6" data-testid="ai-ops-page">
      <header>
        <div className="tick-label text-sky-400 mb-1">/ Internal AI Operations</div>
        <h1 className="font-display font-black text-3xl text-slate-100 leading-tight">Eight role-specific copilots. Read-only.</h1>
        <p className="text-slate-400 text-sm mt-1 max-w-2xl">
          Phase 4 ships these as deterministic, rule-based summarizers. They read your own internal data, never write back, never send email, never call a public LLM. Phase 5+ may swap selected copilots to an LLM provider behind the same UI.
        </p>
        <div className="mt-3 inline-flex items-center gap-2 border border-rose-500/30 bg-rose-500/5 text-rose-200 px-3 py-1.5 font-mono text-[10px] tracking-widest uppercase">
          <ShieldAlert className="w-3 h-3" /> Internal Boundary · No mutation · No email · No external side effects
        </div>
      </header>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-soft)]">
        {COPILOTS.map((c) => {
          const Icon = c.icon;
          return (
            <button key={c.id} onClick={() => setOpen(open === c.id ? null : c.id)} data-testid={`ai-ops-card-${c.id}`} className={`bg-[var(--bg-secondary)] p-5 text-left hover:bg-slate-900/70 transition-colors flex flex-col ${open === c.id ? "ring-1 ring-sky-500/60" : ""}`}>
              <Icon className="w-5 h-5 text-sky-400 mb-3" strokeWidth={1.8} />
              <div className="font-display font-bold text-slate-100 text-base mb-1">{c.label}</div>
              <div className="text-xs text-slate-400 leading-relaxed flex-1">{c.description}</div>
              <div className="mt-3 font-mono text-[10px] tracking-widest uppercase text-sky-400 inline-flex items-center gap-1">
                {open === c.id ? "Hide insights" : "Show insights"} <ArrowRight className="w-3 h-3" />
              </div>
            </button>
          );
        })}
      </div>

      {open && (
        <section className="bg-[var(--bg-secondary)] p-5 border border-sky-500/30" data-testid={`ai-ops-detail-${open}`}>
          <div className="tick-label text-sky-400 mb-3">/ {COPILOTS.find((c) => c.id === open).label}</div>
          {renderCopilot(open, insights)}
        </section>
      )}
    </div>
  );
}

function renderCopilot(id, ins) {
  if (!ins) return null;

  switch (id) {
    case "sales":
      return (
        <div className="grid md:grid-cols-2 gap-px bg-[var(--border-soft)]">
          <Sub title="Top 6 active priorities">
            {ins.sales_priorities.length === 0 ? <I>No active prospects yet.</I> : (
              <ul className="space-y-1.5 text-sm">
                {ins.sales_priorities.map((p) => (
                  <li key={p.id}>
                    <Link to={`/internal/prospects/${p.id}`} className="text-slate-100 hover:text-sky-300">{p.name}</Link>
                    <span className="text-slate-500 text-xs ml-2">{p.stage} · {p.priority || "—"}</span>
                  </li>
                ))}
              </ul>
            )}
          </Sub>
          <Sub title="Stalled (no review &gt; 14 days)">
            {ins.stalled.length === 0 ? <I>None.</I> : (
              <ul className="space-y-1.5 text-sm">
                {ins.stalled.map((p) => (
                  <li key={p.id}>
                    <Link to={`/internal/prospects/${p.id}`} className="text-slate-100 hover:text-sky-300">{p.name}</Link>
                    <span className="text-rose-300 text-xs ml-2">{p.days_idle}d idle</span>
                  </li>
                ))}
              </ul>
            )}
          </Sub>
        </div>
      );

    case "marketing":
      return <Placeholder note="Public AI Discovery starts / completions / fit-summary events are tracked to /api/events. A CEO-only session-authenticated read endpoint for those events is pending Phase 5. Once available, this copilot will summarize daily/weekly funnel deltas." />;

    case "success":
      return ins.customer_activity ? (
        <div className="grid sm:grid-cols-3 gap-px bg-[var(--border-soft)]">
          <Stat k="Paid Customers"      v={ins.customer_activity.paid_customers} />
          <Stat k="Repeat Customers"    v={ins.customer_activity.repeat_customers} />
          <Stat k="Proposals Sent"      v={ins.customer_activity.assessments_delivered} />
        </div>
      ) : <I>No customer rollup data available.</I>;

    case "support":
      return <Placeholder note="No support inbox is wired today. Once a support channel exists (Zendesk / Front / email), this copilot will summarize themes, response times, and recurring blockers." />;

    case "data": {
      // Recompute on the fly so we don't store extra state
      // (We don't have prospects in scope here; show counts from insights only.)
      return (
        <div className="space-y-2 text-sm">
          <div className="text-slate-300">Data-quality scan runs against your prospect list and surfaces incomplete records on the Prospects page (look for missing decision-maker, website, or NAICS).</div>
          <Link to="/internal/prospects" className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase text-sky-400 hover:text-sky-300">Open Prospects <ArrowRight className="w-3 h-3" /></Link>
        </div>
      );
    }

    case "product":
      return <Placeholder note="Feature-interest signals will be derived from AI Discovery answers (most-cited challenges) and Academy article views once the session-authenticated /api/events read path ships in Phase 5." />;

    case "revenue":
      return (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-3 gap-px bg-[var(--border-soft)]">
            <Stat k="Paid Customers" v={ins.customer_activity?.paid_customers ?? 0} />
            <Stat k="Active Pipeline" v={ins.sales_priorities.length} />
            <Stat k="Hot Prospects" v={ins.hot_prospects.length} />
          </div>
          <div className="text-xs text-slate-500 leading-relaxed">
            Live Stripe rollup (MRR / ARR / refund rate) deferred to Phase 5. CEO-only by design — the existing <code className="text-sky-400">/api/internal/customers</code> view shows the per-customer detail.
          </div>
          <Link to="/internal/customers" className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase text-sky-400 hover:text-sky-300">Open Customers View <ArrowRight className="w-3 h-3" /></Link>
        </div>
      );

    case "ops":
      return (
        <ul className="space-y-1.5">
          {ins.system_health.map((s, i) => (
            <li key={i} className="flex items-start justify-between gap-3 text-sm">
              <div>
                <div className="text-slate-100">{s.label}</div>
                <div className="text-xs text-slate-500">{s.detail}</div>
              </div>
              <span className={`font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 border shrink-0 ${{ ok:"text-emerald-400 border-emerald-500/40", pending:"text-amber-400 border-amber-500/40", todo:"text-rose-400 border-rose-500/40" }[s.status]}`}>{s.status}</span>
            </li>
          ))}
        </ul>
      );

    default:
      return null;
  }
}

const Sub = ({ title, children }) => (
  <div className="bg-[var(--bg-secondary)] p-4">
    <div className="font-mono text-[10px] tracking-widest uppercase text-sky-400 mb-2">/ {title}</div>
    {children}
  </div>
);
const Stat = ({ k, v }) => (
  <div className="bg-[var(--bg-secondary)] p-4">
    <div className="tick-label">{k}</div>
    <div className="metric-number text-2xl text-sky-400">{v}</div>
  </div>
);
const I = ({ children }) => <div className="text-sm text-slate-500 italic">{children}</div>;
const Placeholder = ({ note }) => (
  <div className="border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-slate-300 leading-relaxed flex items-start gap-2">
    <Brain className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
    <span><span className="font-mono text-[10px] tracking-widest uppercase text-amber-300 mr-1">Placeholder ·</span>{note}</span>
  </div>
);
